import { format, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const LOCALE = 'en-US';

const DEFAULT_DATE_SETTINGS: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: undefined,
  second: undefined,
  hour12: false,
};

const DEFAULT_NUMBER_SETTINGS: Intl.NumberFormatOptions = {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2,
};

const DATE_FORMAT = {
  YYYY_MM_DD: 'yyyy-MM-dd',
  YYYY_MM: 'yyyy-MM',
  'MM/dd/yyyy': 'MM/dd/yyyy',
};

// Validation patterns for each date format to detect if value already matches the target format.
const FORMAT_PATTERNS: Record<ValueOf<typeof DATE_FORMAT>, RegExp> = {
  'yyyy-MM-dd': /^\d{4}-\d{2}-\d{2}$/,
  'yyyy-MM': /^\d{4}-\d{2}$/,
  'MM/dd/yyyy': /^\d{2}\/\d{2}\/\d{4}$/,
};

// Fallback map used only when Intl can't resolve a symbol for `currency` (invalid/unsupported code).
const CURRENCY_SYMBOL_FALLBACK: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  CHF: 'CHF',
};

// Cached for the default locale so formatDateRange doesn't recreate an Intl.DateTimeFormat on every call.
const dateRangeFormatter = new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });

export class Formatter {
  static DATE_FORMAT = DATE_FORMAT;
  static CURRENCY_SYMBOL_FALLBACK = CURRENCY_SYMBOL_FALLBACK;
  static secondsToString = secondsToString;
  static getRelativeTimeAgo = getRelativeTimeAgo;
  static toFilterDateTime = toFilterDateTime;
  static toUTC = toUTC;
  static dateFormat = (date: Date, pattern: ValueOf<typeof DATE_FORMAT> = DATE_FORMAT.YYYY_MM_DD) => {
    return format(date, pattern);
  };

  // Converts a date string (YYYY-MM-DD) to ISO format with midnight UTC time
  // Example: "2025-05-01" => "2025-05-01T00:00:00Z"
  static prettifyToISODate(dateString: string): string {
    const date = parseISO(dateString);
    if (!isValid(date)) {
      throw new Error('Invalid date string');
    }

    // Append the UTC midnight time portion to the date string
    return `${dateString}T00:00:00Z`;
  }

  static prettifyToGMTDate(dateString: string): string {
    const date = parseISO(dateString);
    if (!isValid(date)) {
      throw new Error('Invalid date string');
    }

    // If already contains a time component, strip trailing Z and return as-is
    if (dateString.includes('T')) {
      return dateString.replace(/Z$/, '');
    }

    // Append the UTC midnight time portion to the date string
    return `${dateString}T00:00:00`;
  }

  // Formats two yyyy-MM-dd (or Date) values into a locale-aware range, e.g. "Aug 22 - 28, 2026".
  static formatDateRange(start: string | Date, end: string | Date, locale: string = LOCALE): string {
    const formatter =
      locale === LOCALE
        ? dateRangeFormatter
        : new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });

    const startDate = typeof start === 'string' ? new Date(Formatter.prettifyToGMTDate(start)) : start;
    const endDate = typeof end === 'string' ? new Date(Formatter.prettifyToGMTDate(end)) : end;

    return formatter.formatRange(startDate, endDate).replace(/\s*–\s*/g, ' - ');
  }

  // Check if `value` matches the date format (e.g. "yyyy-MM-dd").
  static isFormat(value: string, format: ValueOf<typeof DATE_FORMAT> = DATE_FORMAT.YYYY_MM_DD): boolean {
    return FORMAT_PATTERNS[format]?.test(value) ?? false;
  }

  // Normalizes a date string to the target format when parsing succeeds.
  static normalizeDate(
    value: string,
    {
      format = DATE_FORMAT.YYYY_MM_DD,
      useUtc = false,
    }: { format?: ValueOf<typeof DATE_FORMAT>; useUtc?: boolean } = {},
  ): string {
    if (Formatter.isFormat(value, format)) return value;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    const dateToFormat = useUtc ? Formatter.toUTC(parsed) : parsed;
    return Formatter.dateFormat(dateToFormat, format);
  }

  static getUserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  date: Intl.DateTimeFormat;
  number: Intl.NumberFormat;

  constructor({
    locale = LOCALE,
    dateOptions,
    numberOptions,
  }: { locale?: string; dateOptions?: Intl.DateTimeFormatOptions; numberOptions?: Intl.NumberFormatOptions } = {}) {
    this.date = new Intl.DateTimeFormat(locale, { ...DEFAULT_DATE_SETTINGS });
    this.number = new Intl.NumberFormat(locale, { ...DEFAULT_NUMBER_SETTINGS });

    try {
      this.date = new Intl.DateTimeFormat(locale, {
        ...DEFAULT_DATE_SETTINGS,
        ...dateOptions,
      });
    } catch (e) {
      console.error(e);
    }

    try {
      this.number = new Intl.NumberFormat(locale, {
        ...DEFAULT_NUMBER_SETTINGS,
        ...numberOptions,
      });
    } catch (e) {
      console.error(e);
    }
  }

  static currencyToSymbol(currency: string): string {
    try {
      const parts = new Intl.NumberFormat(LOCALE, { style: 'currency', currency }).formatToParts(0);
      const symbol = parts.find((p) => p.type === 'currency')?.value;
      if (symbol) return symbol;
    } catch {
      // invalid ISO code or `currency` is already a symbol (e.g. '€') — fall through
    }

    return Formatter.CURRENCY_SYMBOL_FALLBACK[currency] ?? currency;
  }

  static symbolToCurrency(symbol: string): string | undefined {
    return Object.keys(Formatter.CURRENCY_SYMBOL_FALLBACK).find(
      (code) => Formatter.CURRENCY_SYMBOL_FALLBACK[code] === symbol,
    );
  }

  static formatCurrency(value: number, currency: 'USD' | 'EUR' = 'USD') {
    const compact = Math.abs(value) >= 1000;
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: Formatter.symbolToCurrency(currency) || currency, // For double check to be sure
      notation: compact ? 'compact' : 'standard',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

function secondsToString(seconds: number) {
  const numYears = Math.floor(seconds / 31536000);
  const numDays = Math.floor((seconds % 31536000) / 86400);
  const numHours = Math.floor(((seconds % 31536000) % 86400) / 3600);
  const numMinutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60);
  const numSeconds = (((seconds % 31536000) % 86400) % 3600) % 60;
  const parts = [];
  if (numYears) {
    parts.push(numYears + 'y');
  }
  if (numDays) {
    parts.push(numDays + 'd');
  }
  if (numHours) {
    parts.push(numHours + 'h');
  }
  if (numMinutes) {
    parts.push(numMinutes + 'min');
  }
  if (numSeconds) {
    parts.push(numSeconds + 'sec');
  }
  return parts.join(' ');
}

function getRelativeTimeAgo(date: string) {
  const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];
  const units: Intl.RelativeTimeFormatUnit[] = ['second', 'minute', 'hour', 'day', 'week', 'month', 'year'];

  const deltaSeconds = Math.round((parseISO(date).valueOf() - Date.now()) / 1000);
  const unitIndex = cutoffs.findIndex((cutoff) => cutoff > Math.abs(deltaSeconds));
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
  const inPast = deltaSeconds < 0;

  const unit = units[unitIndex];
  const valueExact = Math.abs(deltaSeconds) / divisor;
  const valueFloor = Math.floor(valueExact);
  const fractionalPart = valueExact - valueFloor;

  // Use approximate language for values between units when the fractional part is small
  if (valueFloor >= 1 && valueFloor <= 3 && fractionalPart > 0 && fractionalPart < 0.35) {
    if (valueFloor > 1) {
      return inPast ? `over ${valueFloor} ${unit}s ago` : `in over ${valueFloor} ${unit}s`;
    }
    return inPast ? `over a ${unit} ago` : `in over a ${unit}`;
  }

  return new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' }).format(
    Math.floor(deltaSeconds / divisor),
    units[unitIndex],
  );
}

function toFilterDateTime(date: Date | string) {
  return format(typeof date === 'string' ? parseISO(date) : date, "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

function toUTC(date: Date | string) {
  return toZonedTime(date, 'UTC');
}
